import React, { useContext } from 'react';
import { Grid, IconButton, MenuItem, TextField, Tooltip } from '@material-ui/core';
import { InfoOutlined } from '@material-ui/icons';
import { css } from '@emotion/react';
import { formatInTimeZone } from 'date-fns-tz';
import { Alert, AlertTitle } from '@material-ui/lab';
import { asDateInTZ } from '@/utilities/convertToISO';
import { MANIFEST_TYPE_LABELS, MANIFEST_TYPE_TOOLTIPS, RETURN_MANIFEST_TYPE } from '@/constants/manifestStatuses';
import { Context } from '../store';

// `min-width: 0` is load-bearing: without it a MUI TextField won't shrink below its intrinsic
// width and the row pushes the dialog past its max-width. The 160px basis lets fields wrap instead.
const fieldStyles = css`
    flex: 1 1 160px;
    min-width: 0;
`;

const CreateManifestFields = ({ isInbound, directionTab, onTypeChange }) => {
    const { state, callbacks } = useContext(Context);

    return (
        <Grid
            container
            direction="row"
            css={css`
                gap: 0.75rem;
                flex-wrap: wrap;
                margin-bottom: 1rem;
            `}
        >
            <TextField
                variant="outlined"
                required
                label="Manifest name"
                name="name"
                InputLabelProps={{ shrink: true }}
                value={state.newManifest?.name || ''}
                onChange={(e) => callbacks.setNewManifest((prev) => ({ ...prev, name: e.target.value }))}
                error={!state.newManifest?.name}
                css={fieldStyles}
            />
            <TextField
                type="date"
                variant="outlined"
                required
                label={isInbound ? 'Receiving date' : 'Staging date'}
                name="receiving_date"
                InputLabelProps={{ shrink: true }}
                value={
                    state.newManifest?.receiving_date
                        ? formatInTimeZone(new Date(state.newManifest.receiving_date), 'UTC', 'yyyy-MM-dd')
                        : ''
                }
                onChange={(e) =>
                    callbacks.setNewManifest((prev) => ({
                        ...prev,
                        receiving_date: asDateInTZ(e.target.value, 'UTC').toISOString(),
                    }))
                }
                error={!state.newManifest?.receiving_date}
                css={fieldStyles}
            />
            {state.warehouses.length > 0 ? (
                <TextField
                    variant="outlined"
                    required
                    select
                    label="Warehouse"
                    name="warehouse_id"
                    InputLabelProps={{ shrink: true }}
                    value={state.newManifest?.warehouse_id || ''}
                    onChange={(e) => callbacks.setNewManifest((prev) => ({ ...prev, warehouse_id: e.target.value }))}
                    error={!state.newManifest?.warehouse_id}
                    css={fieldStyles}
                >
                    {state.warehouses.map((warehouse) => (
                        <MenuItem key={warehouse.location_id} value={warehouse.location_id}>
                            {warehouse.location_name}
                        </MenuItem>
                    ))}
                </TextField>
            ) : (
                <Alert severity="warning" css={fieldStyles}>
                    <AlertTitle>No Warehouse(s)</AlertTitle>Please add a new warehouse to get started
                </Alert>
            )}
            <TextField
                variant="outlined"
                required
                select
                label="Type"
                name="type"
                InputLabelProps={{ shrink: true }}
                disabled={!!state.newManifest?.isDraft || !!state.newManifest?.isUpdate}
                value={state.newManifest?.type || ''}
                onChange={(e) => onTypeChange(e.target.value)}
                error={!state.newManifest?.type}
                css={fieldStyles}
            >
                {/* Return is a display type over route-linked INBOUND; it can't be created by hand. */}
                {directionTab.typeOptions
                    .filter((type) => type !== RETURN_MANIFEST_TYPE)
                    .map((type) => (
                        <MenuItem key={type} value={type}>
                            {[MANIFEST_TYPE_LABELS[type], MANIFEST_TYPE_TOOLTIPS[type]].filter((x) => !!x).join(' ')}
                        </MenuItem>
                    ))}
            </TextField>
            <Grid
                item
                css={css`
                    ${fieldStyles}
                    display: flex;
                    align-items: center;
                `}
            >
                <TextField
                    variant="outlined"
                    select
                    label="Share manifest"
                    name="share"
                    disabled={state.autoShareArmed}
                    value={state.autoShareArmed ? true : state.newManifest?.share_manifest || false}
                    onChange={(e) =>
                        callbacks.setNewManifest((prev) => ({
                            ...prev,
                            share_manifest: e.target.value,
                        }))
                    }
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                >
                    <MenuItem value={true}>Yes</MenuItem>
                    <MenuItem value={false}>No</MenuItem>
                </TextField>
                <Tooltip
                    style={{ color: '#59B863' }}
                    title={
                        state.autoShareArmed
                            ? 'This manifest contains items from a partnered shipper with manifest sharing enabled, so it will be shared automatically.'
                            : 'Optionally choose to share the status of this manifest with the shippers of the included orders. They will only be able to see details for their own orders/items.'
                    }
                    placement="top"
                >
                    <IconButton size="small">
                        <InfoOutlined />
                    </IconButton>
                </Tooltip>
            </Grid>
        </Grid>
    );
};

export default CreateManifestFields;
